<template>
  <v-container fluid class="app-add-page">
    <div class="app-add-page__inner">
    <!-- Confirmation Dialog -->
    <TheModalConfirmation ref="confirmDialog" />

    <!-- Page hero. No surface of its own: the sheet under it is the panel. -->
    <div class="add-hero">
      <button
        type="button"
        class="back-btn"
        aria-label="Back"
        @click="handleBack()"
      >
        <v-icon size="20">mdi-arrow-left</v-icon>
      </button>
      <div class="add-hero__info">
        <span class="add-hero__eyebrow">New HRDC Application</span>
        <h1 class="add-hero__title">{{ steps[currentPage - 1] }}</h1>
      </div>
      <span class="add-hero__step">
        Step {{ currentPage }}<span class="add-hero__step-total">/{{ steps.length }}</span>
      </span>
    </div>

    <!-- Stepper Progress -->
    <div class="stepper">
      <div class="stepper__track">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="stepper__step"
        >
          <div
            class="stepper__circle"
            :class="{
              'stepper__circle--active': currentPage === index + 1,
              'stepper__circle--completed': currentPage > index + 1,
              'stepper__circle--upcoming': currentPage < index + 1,
            }"
          >
            <v-icon v-if="currentPage > index + 1" size="16">mdi-check</v-icon>
            <span v-else class="stepper__number">{{ index + 1 }}</span>
          </div>
          <span
            class="stepper__label"
            :class="{
              'stepper__label--active': currentPage === index + 1,
              'stepper__label--completed': currentPage > index + 1,
            }"
          >{{ step }}</span>
          <div
            v-if="index < steps.length - 1"
            class="stepper__line"
            :class="{ 'stepper__line--completed': currentPage > index + 1 }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Page 1: Sales Information -->
    <transition name="slide-fade" mode="out-in">
      <div v-if="currentPage === 1" key="page1">
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-briefcase-outline</v-icon>
            <span class="section-header__title">Sales Information</span>
          </div>
          <div class="section-body">
            <!-- Company Name -->
            <div class="form-group">
              <label class="form-group__label">Company Name <span class="required">*</span></label>
              <v-textarea
                v-model="formData.companyName"
                outlined
                dense
                rows="2"
                auto-grow
                placeholder="e.g. XXX SDN. BHD."
                hide-details="auto"
                :error-messages="errorField.companyName"
                class="app-input"
              ></v-textarea>
            </div>

            <!-- Company SSM Number -->
            <div class="form-group">
              <label class="form-group__label">Company SSM Number <span class="required">*</span></label>
              <v-textarea
                v-model="formData.companySsmNo"
                outlined
                dense
                rows="2"
                auto-grow
                placeholder="Enter SSM number"
                hide-details="auto"
                :error-messages="errorField.companySsmNo"
                class="app-input"
              ></v-textarea>
            </div>

            <!-- Company Address -->
            <div class="form-group">
              <label class="form-group__label">Company Address <span class="required">*</span></label>
              <v-textarea
                v-model="formData.companyAddress"
                outlined
                dense
                rows="3"
                auto-grow
                placeholder="Enter full company address"
                hide-details="auto"
                :error-messages="errorField.companyAddress"
                class="app-input"
              ></v-textarea>
            </div>

            <!-- Company TIN Number -->
            <div class="form-group">
              <label class="form-group__label">Company TIN Number <span class="required">*</span></label>
              <v-text-field
                v-model="formData.companyTinNo"
                outlined
                dense
                placeholder="Enter TIN number"
                hide-details="auto"
                :error-messages="errorField.companyTinNo"
                class="app-input"
              ></v-text-field>
            </div>

            <!-- taxPOD Type -->
            <div class="form-group">
              <label class="form-group__label">taxPOD Type <span class="required">*</span></label>
              <v-select
                v-model="formData.taxpodType"
                :items="taxpodTypeItems"
                outlined
                dense
                placeholder="Select taxPOD type"
                hide-details="auto"
                :error-messages="errorField.taxpodType"
                class="app-input"
              ></v-select>
            </div>

            <!-- Price -->
            <div class="form-group">
              <label class="form-group__label">Price <span class="required">*</span></label>
              <v-select
                v-model="formData.price"
                :items="priceItems"
                outlined
                dense
                placeholder="Select price"
                hide-details="auto"
                :error-messages="errorField.price"
                class="app-input"
              ></v-select>
            </div>

            <!-- Custom Price (if Other) -->
            <div class="form-group form-group--indent" v-if="formData.price === 'Other'">
              <label class="form-group__label">Custom Price <span class="required">*</span></label>
              <v-text-field
                v-model="formData.customPrice"
                outlined
                dense
                placeholder="Enter custom price (e.g. RM 3,500)"
                hide-details="auto"
                :error-messages="errorField.customPrice"
                class="app-input"
              ></v-text-field>
            </div>

            <!-- Has client made payment -->
            <div class="form-group">
              <label class="form-group__label">Has your client made payment for taxPOD? <span class="required">*</span></label>
              <v-select
                v-model="formData.isPaymentMade"
                :items="yesNoItems"
                outlined
                dense
                placeholder="Select"
                hide-details="auto"
                :error-messages="errorField.isPaymentMade"
                class="app-input"
              ></v-select>
            </div>

            <!-- Payment Mode (if Yes) -->
            <div class="form-group" v-if="formData.isPaymentMade === 'Yes'">
              <label class="form-group__label">Payment Mode <span class="required">*</span></label>
              <v-select
                v-model="formData.paymentMode"
                :items="paymentModeItems"
                outlined
                dense
                placeholder="Select payment mode"
                hide-details="auto"
                :error-messages="errorField.paymentMode"
                class="app-input"
              ></v-select>
            </div>

            <!-- Custom Payment Mode (if Other) -->
            <div class="form-group form-group--indent" v-if="formData.isPaymentMade === 'Yes' && formData.paymentMode === 'Other'">
              <label class="form-group__label">Custom Payment Mode <span class="required">*</span></label>
              <v-text-field
                v-model="formData.customPaymentMode"
                outlined
                dense
                placeholder="Enter payment mode"
                hide-details="auto"
                :error-messages="errorField.customPaymentMode"
                class="app-input"
              ></v-text-field>
            </div>
          </div>
        </div>

        <!-- Next Button -->
        <div class="form-actions">
          <div></div>
          <button class="action-btn action-btn--primary" @click="nextPage()">
            Next
            <v-icon size="18" class="ml-1">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>
    </transition>

    <!-- Page 2: Conditional -->
    <transition name="slide-fade" mode="out-in">
      <div v-if="currentPage === 2" key="page2">
        <!-- If payment = Yes: Bank Account Details -->
        <div v-if="formData.isPaymentMade === 'Yes'" class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-bank-outline</v-icon>
            <span class="section-header__title">Client Bank Account Details</span>
          </div>
          <div class="section-body">
            <!-- Bank Account Screenshot -->
            <div class="form-group">
              <label class="form-group__label">Bank Account Screenshot <span class="required">*</span></label>
              <div
                class="file-upload-area"
                :class="{
                  'file-upload-area--error': errorField.bankAccountScreenshot,
                  'file-upload-area--drag': fileDragOver,
                }"
                @click="$refs.bankScreenshotInput.click()"
                @dragover.prevent="fileDragOver = true"
                @dragleave.prevent="fileDragOver = false"
                @drop.prevent="handleFileDrop($event)"
              >
                <input
                  ref="bankScreenshotInput"
                  type="file"
                  accept="image/*,.pdf"
                  style="display: none"
                  @change="handleFileSelect($event)"
                />
                <div v-if="!formData.bankAccountScreenshot" class="file-upload-area__placeholder">
                  <v-icon size="28" color="var(--app-text-faint)">mdi-cloud-upload-outline</v-icon>
                  <span class="file-upload-area__text">Click or drag file to upload</span>
                  <span class="file-upload-area__hint">Supports: Images, PDF</span>
                </div>
                <div v-else class="file-upload-area__selected">
                  <v-icon size="20" color="var(--app-accent-strong)">mdi-file-check-outline</v-icon>
                  <span class="file-upload-area__filename">{{ formData.bankAccountScreenshot.name }}</span>
                  <v-icon
                    size="18"
                    color="var(--app-text-faint)"
                    class="file-upload-area__remove"
                    @click.stop="formData.bankAccountScreenshot = null"
                  >mdi-close-circle</v-icon>
                </div>
              </div>
              <div v-if="errorField.bankAccountScreenshot" class="file-upload-error">{{ errorField.bankAccountScreenshot }}</div>
              <span class="form-group__helper">Please name the attachment as per the client Company Name</span>
            </div>

            <!-- Specific Bank Name -->
            <div class="form-group">
              <label class="form-group__label">Specific Bank Name <span class="required">*</span></label>
              <v-select
                v-model="formData.specificBankName"
                :items="bankNameItems"
                outlined
                dense
                placeholder="Search or select bank"
                hide-details="auto"
                :error-messages="errorField.specificBankName"
                class="app-input"
              ></v-select>
            </div>

            <!-- Custom Bank Name (if Other) -->
            <div class="form-group form-group--indent" v-if="formData.specificBankName === 'Other'">
              <label class="form-group__label">Other Bank Name <span class="required">*</span></label>
              <v-text-field
                v-model="formData.customBankName"
                outlined
                dense
                placeholder="Enter bank name"
                hide-details="auto"
                :error-messages="errorField.customBankName"
                class="app-input"
              ></v-text-field>
            </div>

            <!-- Account Number -->
            <div class="form-group">
              <label class="form-group__label">Account Number <span class="required">*</span></label>
              <v-text-field
                v-model="formData.accountNumber"
                outlined
                dense
                placeholder="Enter account number"
                hide-details="auto"
                :error-messages="errorField.accountNumber"
                class="app-input"
              ></v-text-field>
              <span class="form-group__helper">Only number without any symbol.</span>
            </div>

            <!-- Account Holder Name -->
            <div class="form-group">
              <label class="form-group__label">Account Holder Name <span class="required">*</span></label>
              <v-text-field
                v-model="formData.accountHolderName"
                outlined
                dense
                placeholder="Enter account holder name"
                hide-details="auto"
                :error-messages="errorField.accountHolderName"
                class="app-input"
              ></v-text-field>
            </div>
          </div>
        </div>

        <!-- If payment = No: Enrollment Details -->
        <div v-if="formData.isPaymentMade === 'No'" class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-account-plus-outline</v-icon>
            <span class="section-header__title">taxPOD Account Enrollment Details</span>
          </div>
          <div class="section-body">
            <div class="section-description">
              Please submit client's details for TaxPOD Enrollment. We will enroll once we receive funds from HRDC.
            </div>

            <!-- Number of Pax -->
            <div class="form-group">
              <label class="form-group__label">Number of Pax <span class="required">*</span></label>
              <v-select
                v-model="numberOfPax"
                :items="paxOptions"
                outlined
                dense
                hide-details="auto"
                class="app-input"
              ></v-select>
            </div>

            <!-- Account 1 (always rendered, persisted on Application table) -->
            <div :class="numberOfPax >= 2 ? 'participant-block' : ''">
              <div v-if="numberOfPax >= 2" class="participant-block__badge">Account 1</div>

              <!-- First Name -->
              <div class="form-group">
                <label class="form-group__label">First Name <span class="required">*</span></label>
                <v-textarea
                  v-model="formData.firstNameAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter first name"
                  hide-details="auto"
                  :error-messages="errorField.firstNameAccEnrollment"
                  class="app-input"
                ></v-textarea>
              </div>

              <!-- Last Name -->
              <div class="form-group">
                <label class="form-group__label">Last Name <span class="required">*</span></label>
                <v-textarea
                  v-model="formData.lastNameAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter last name"
                  hide-details="auto"
                  :error-messages="errorField.lastNameAccEnrollment"
                  class="app-input"
                ></v-textarea>
              </div>

              <!-- Contact Number -->
              <div class="form-group">
                <label class="form-group__label">Contact Number <span class="required">*</span></label>
                <v-textarea
                  v-model="formData.contactNoAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter contact number"
                  hide-details="auto"
                  :error-messages="errorField.contactNoAccEnrollment"
                  class="app-input"
                ></v-textarea>
                <span class="form-group__helper">Mobile number for WhatsApp</span>
              </div>

              <!-- Email -->
              <div class="form-group">
                <label class="form-group__label">Email (for taxPOD login purpose) <span class="required">*</span></label>
                <v-textarea
                  v-model="formData.emailAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter email address"
                  hide-details="auto"
                  :error-messages="errorField.emailAccEnrollment"
                  class="app-input"
                ></v-textarea>
              </div>
            </div>

            <!-- Additional Accounts (Account 2..N) -->
            <div
              v-for="(acct, idx) in additionalEnrollmentDetails"
              :key="idx"
              class="participant-block"
            >
              <div class="participant-block__badge">Account {{ idx + 2 }}</div>

              <div class="form-group">
                <label class="form-group__label">First Name <span class="required">*</span></label>
                <v-textarea
                  v-model="acct.firstNameAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter first name"
                  hide-details="auto"
                  :error-messages="additionalEnrollmentErrors[idx] && additionalEnrollmentErrors[idx].firstNameAccEnrollment"
                  class="app-input"
                ></v-textarea>
              </div>

              <div class="form-group">
                <label class="form-group__label">Last Name <span class="required">*</span></label>
                <v-textarea
                  v-model="acct.lastNameAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter last name"
                  hide-details="auto"
                  :error-messages="additionalEnrollmentErrors[idx] && additionalEnrollmentErrors[idx].lastNameAccEnrollment"
                  class="app-input"
                ></v-textarea>
              </div>

              <div class="form-group">
                <label class="form-group__label">Contact Number <span class="required">*</span></label>
                <v-textarea
                  v-model="acct.contactNoAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter contact number"
                  hide-details="auto"
                  :error-messages="additionalEnrollmentErrors[idx] && additionalEnrollmentErrors[idx].contactNoAccEnrollment"
                  class="app-input"
                ></v-textarea>
                <span class="form-group__helper">Mobile number for WhatsApp</span>
              </div>

              <div class="form-group">
                <label class="form-group__label">Email (for taxPOD login purpose) <span class="required">*</span></label>
                <v-textarea
                  v-model="acct.emailAccEnrollment"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  placeholder="Enter email address"
                  hide-details="auto"
                  :error-messages="additionalEnrollmentErrors[idx] && additionalEnrollmentErrors[idx].emailAccEnrollment"
                  class="app-input"
                ></v-textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Previous + Next Buttons -->
        <div class="form-actions">
          <button class="action-btn action-btn--secondary" @click="prevPage()">
            <v-icon size="18" class="mr-1">mdi-arrow-left</v-icon>
            Previous
          </button>
          <button class="action-btn action-btn--primary" @click="nextPage()">
            Next
            <v-icon size="18" class="ml-1">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>
    </transition>

    <!-- Page 3: Client Details -->
    <transition name="slide-fade" mode="out-in">
      <div v-if="currentPage === 3" key="page3">
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-account-tie-outline</v-icon>
            <span class="section-header__title">Client Details</span>
          </div>
          <div class="section-body">
            <div class="section-description">
              Please submit client's side details of Person in Charge of HRDC Application. We will send HRDC documents to this contact person.
            </div>

            <!-- Name (PIC) -->
            <div class="form-group">
              <label class="form-group__label">Name (Person in Charge of HRDC Application) <span class="required">*</span></label>
              <v-textarea
                v-model="formData.picName"
                outlined
                dense
                rows="2"
                auto-grow
                placeholder="Enter name of person in charge"
                hide-details="auto"
                :error-messages="errorField.picName"
                class="app-input"
              ></v-textarea>
            </div>

            <!-- Contact Number -->
            <div class="form-group">
              <label class="form-group__label">Contact Number <span class="required">*</span></label>
              <v-textarea
                v-model="formData.picContactNumber"
                outlined
                dense
                rows="2"
                auto-grow
                placeholder="e.g. 60123456789"
                hide-details="auto"
                :error-messages="errorField.picContactNumber"
                class="app-input"
              ></v-textarea>
              <span class="form-group__helper">Please add country code and remove all symbols and spaces, e.g. 60123456789</span>
            </div>

            <!-- To Email (mandatory, multi-tag) -->
            <div class="form-group">
              <label class="form-group__label">
                To Email <span class="required">*</span>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      size="16"
                      class="form-group__info"
                      v-on="on"
                    >mdi-information-outline</v-icon>
                  </template>
                  <span>Press <kbd>Tab</kbd> or <kbd>Enter</kbd> to add multiple emails</span>
                </v-tooltip>
              </label>
              <div
                class="tag-input"
                :class="{ 'tag-input--error': errorField.picEmailTo }"
                @click="focusToInput()"
              >
                <span
                  v-for="(val, i) in formData.picEmailTo"
                  :key="'picEmailTo-' + i"
                  class="tag-input__chip"
                >
                  {{ val }}
                  <button
                    type="button"
                    class="tag-input__chip-remove"
                    @click.stop="removePicEmailTo(i)"
                  >
                    <v-icon size="14" color="var(--app-text-muted)">mdi-close</v-icon>
                  </button>
                </span>
                <input
                  ref="picEmailToInput"
                  v-model="picEmailToInput"
                  type="text"
                  class="tag-input__input"
                  :placeholder="formData.picEmailTo.length === 0 ? 'Enter email address' : ''"
                  @keydown.enter.prevent="addPicEmailToTag()"
                  @keydown.tab.prevent="addPicEmailToTag()"
                  @blur="addPicEmailToTag()"
                />
              </div>
              <div v-if="errorField.picEmailTo" class="tag-input__error">{{ errorField.picEmailTo }}</div>
            </div>

            <!-- Cc Email (optional, multi-tag) -->
            <div class="form-group">
              <label class="form-group__label">
                Cc Email
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      size="16"
                      class="form-group__info"
                      v-on="on"
                    >mdi-information-outline</v-icon>
                  </template>
                  <span>Press <kbd>Tab</kbd> or <kbd>Enter</kbd> to add multiple emails</span>
                </v-tooltip>
              </label>
              <div
                class="tag-input"
                :class="{ 'tag-input--error': errorField.picEmailCc }"
                @click="focusCcInput()"
              >
                <span
                  v-for="(val, i) in formData.picEmailCc"
                  :key="'picEmailCc-' + i"
                  class="tag-input__chip"
                >
                  {{ val }}
                  <button
                    type="button"
                    class="tag-input__chip-remove"
                    @click.stop="removePicEmailCc(i)"
                  >
                    <v-icon size="14" color="var(--app-text-muted)">mdi-close</v-icon>
                  </button>
                </span>
                <input
                  ref="picEmailCcInput"
                  v-model="picEmailCcInput"
                  type="text"
                  class="tag-input__input"
                  :placeholder="formData.picEmailCc.length === 0 ? 'Optional — enter email address' : ''"
                  @keydown.enter.prevent="addPicEmailCcTag()"
                  @keydown.tab.prevent="addPicEmailCcTag()"
                  @blur="addPicEmailCcTag()"
                />
              </div>
              <div v-if="errorField.picEmailCc" class="tag-input__error">{{ errorField.picEmailCc }}</div>
            </div>
          </div>
        </div>

        <!-- Previous + Submit Buttons -->
        <div class="form-actions">
          <button class="action-btn action-btn--secondary" @click="prevPage()">
            <v-icon size="18" class="mr-1">mdi-arrow-left</v-icon>
            Previous
          </button>
          <button class="action-btn action-btn--submit" @click="toggleSubmitDialog()">
            <v-icon size="18" class="mr-1">mdi-check-circle-outline</v-icon>
            Submit
          </button>
        </div>
      </div>
    </transition>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
    </div>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";

export default {
  components: {
    TheModalConfirmation,
  },
  props: ["params"],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    steps() {
      const step2Label =
        this.formData.isPaymentMade === "Yes"
          ? "Bank Account Details"
          : "Enrollment Details";
      return ["Sales Information", step2Label, "Client Details"];
    },
  },
  data: () => ({
    apiSubmit: new Api(),
    model: new Model(),
    service: new Service(),
    loadingDialog: false,
    currentPage: 1,
    submitDialog: false,
    _hasValidated: false,
    fileDragOver: false,
    // Frontend-only pax counter for the enrollment section (not persisted).
    // When >= 2, additional accounts beyond the first are collected in
    // additionalEnrollmentDetails and sent to the backend as a JSON array.
    numberOfPax: 1,
    paxOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    additionalEnrollmentDetails: [],
    additionalEnrollmentErrors: [],
    formData: {
      companyName: null,
      companySsmNo: null,
      companyAddress: null,
      companyTinNo: null,
      taxpodType: null,
      price: null,
      customPrice: null,
      isPaymentMade: null,
      paymentMode: null,
      customPaymentMode: null,
      // Bank details (if payment = Yes)
      bankAccountScreenshot: null,
      specificBankName: null,
      customBankName: null,
      accountNumber: null,
      accountHolderName: null,
      // Enrollment first account (if payment = No) — persisted on Application
      firstNameAccEnrollment: null,
      lastNameAccEnrollment: null,
      contactNoAccEnrollment: null,
      emailAccEnrollment: null,
      // Client side
      picName: null,
      picContactNumber: null,
      picEmailTo: [],
      picEmailCc: [],
    },
    picEmailToInput: '',
    picEmailCcInput: '',
    errorField: {
      companyName: null,
      companySsmNo: null,
      companyAddress: null,
      companyTinNo: null,
      taxpodType: null,
      price: null,
      customPrice: null,
      isPaymentMade: null,
      paymentMode: null,
      customPaymentMode: null,
      bankAccountScreenshot: null,
      specificBankName: null,
      customBankName: null,
      accountNumber: null,
      accountHolderName: null,
      firstNameAccEnrollment: null,
      lastNameAccEnrollment: null,
      contactNoAccEnrollment: null,
      emailAccEnrollment: null,
      picName: null,
      picContactNumber: null,
      picEmailTo: null,
      picEmailCc: null,
    },
    taxpodTypeItems: ["New Sale", "Renewal"],
    priceItems: ["RM 1,998", "RM 2,388", "RM 2,888", "Other"],
    yesNoItems: ["Yes", "No"],
    paymentModeItems: [
      "Bank Transfer",
      "Credit Card",
      "Ipay88",
      "Cheque",
      "Other",
    ],
    bankNameItems: [
      "AmBank Berhad",
      "Alliance Bank Berhad",
      "Al-Rajhi Banking and Investment Corporation (Malaysia) Berhad",
      "Affin Bank Berhad",
      "Agro Bank",
      "Bank of China (Malaysia) Berhad",
      "Bank Muamalat Malaysia Berhad",
      "Bank Islam Malaysia Berhad",
      "Bank Rakyat Berhad",
      "Bank Simpanan Nasional Berhad",
      "Bank of America",
      "Bank of Tokyo-Mitsubishi UFJ (Malaysia) Berhad",
      "BNP Paribas (M) Bhd / BNP Paribas (Islamic)",
      "CIMB Bank Berhad",
      "Citibank",
      "Deutsche Bank (M) Bhd",
      "Hong Leong Bank Berhad/ Hong Leong Finance",
      "HSBC Bank Berhad",
      "Indust & Comm Bank of China (M) Berhad",
      "JP Morgan Chase",
      "Kuwait Finance House",
      "Maybank/ Malayan Banking Berhad",
      "Mizuho Bank (M) Berhad",
      "OCBC Bank Berhad",
      "Public Bank Berhad/ Public Finance Berhad",
      "RHB Bank Berhad",
      "Standard Chartered Bank Berhad",
      "Sumitomo Mitsui Banking Corporation (M) Bhd",
      "The Royal Bank of Scotland Bhd",
      "United Overseas Bank (M) Bhd",
      "China Construction Bank (Malaysia) Berhad",
      "Bangkok Bank Berhad",
      "MBSB",
      "Other",
    ],
  }),
  created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    this.apiSubmit.setUrl(
      `${this.$service[this.service.key]}/v1/en/console/application/create-application`
    );
    this.apiSubmit.setMethod("post");
  },
  watch: {
    'formData': {
      handler(val) {
        for (const key in val) {
          if (!this.errorField[key]) continue;
          const v = val[key];
          // Arrays clear only when non-empty; primitives clear when filled
          if (Array.isArray(v)) {
            if (v.length > 0) this.errorField[key] = null;
          } else if (v !== null && v !== '' && v !== undefined) {
            this.errorField[key] = null;
          }
        }
      },
      deep: true,
    },
    numberOfPax(val) {
      const targetExtras = Math.max(0, Number(val) - 1);
      while (this.additionalEnrollmentDetails.length < targetExtras) {
        this.additionalEnrollmentDetails.push({
          firstNameAccEnrollment: null,
          lastNameAccEnrollment: null,
          contactNoAccEnrollment: null,
          emailAccEnrollment: null,
        });
        this.additionalEnrollmentErrors.push({
          firstNameAccEnrollment: null,
          lastNameAccEnrollment: null,
          contactNoAccEnrollment: null,
          emailAccEnrollment: null,
        });
      }
      while (this.additionalEnrollmentDetails.length > targetExtras) {
        this.additionalEnrollmentDetails.pop();
        this.additionalEnrollmentErrors.pop();
      }
    },
    additionalEnrollmentDetails: {
      handler(arr) {
        arr.forEach((acct, idx) => {
          const errs = this.additionalEnrollmentErrors[idx];
          if (!errs) return;
          for (const key in acct) {
            if (errs[key] && acct[key] !== null && acct[key] !== '' && acct[key] !== undefined) {
              errs[key] = null;
            }
          }
        });
      },
      deep: true,
    },
  },
  methods: {
    clearErrors() {
      for (const key in this.errorField) {
        this.errorField[key] = null;
      }
    },
    validatePage(page) {
      this._hasValidated = true;
      this.clearErrors();
      let hasError = false;
      let requiredFields = [];

      if (page === 1) {
        requiredFields = [
          "companyName",
          "companySsmNo",
          "companyAddress",
          "companyTinNo",
          "taxpodType",
          "price",
          "isPaymentMade",
        ];

        // If price is Other, require customPrice
        if (this.formData.price === "Other") {
          requiredFields.push("customPrice");
        }

        // If payment made = Yes, require paymentMode
        if (this.formData.isPaymentMade === "Yes") {
          requiredFields.push("paymentMode");
          // If paymentMode is Other, require customPaymentMode
          if (this.formData.paymentMode === "Other") {
            requiredFields.push("customPaymentMode");
          }
        }
      } else if (page === 2) {
        if (this.formData.isPaymentMade === "Yes") {
          requiredFields = [
            "bankAccountScreenshot",
            "specificBankName",
            "accountNumber",
            "accountHolderName",
          ];
          if (this.formData.specificBankName === "Other") {
            requiredFields.push("customBankName");
          }
        } else {
          // First account (persisted on Application).
          requiredFields = [
            "firstNameAccEnrollment",
            "lastNameAccEnrollment",
            "contactNoAccEnrollment",
            "emailAccEnrollment",
          ];

          // Additional accounts (persisted on ApplicationEnrollmentDetail).
          const acctFields = [
            "firstNameAccEnrollment",
            "lastNameAccEnrollment",
            "contactNoAccEnrollment",
            "emailAccEnrollment",
          ];
          for (let i = 0; i < this.additionalEnrollmentDetails.length; i++) {
            const acct = this.additionalEnrollmentDetails[i];
            const errs = this.additionalEnrollmentErrors[i];
            for (const f of acctFields) {
              if (acct[f] === null || acct[f] === "" || acct[f] === undefined) {
                errs[f] = "This field is required";
                hasError = true;
              } else {
                errs[f] = null;
              }
            }
          }
        }
      } else if (page === 3) {
        requiredFields = ["picName", "picContactNumber"];
        // Auto-commit any text the user typed but did not press Enter/Tab on
        if ((this.picEmailToInput || '').trim()) this.addPicEmailToTag();
        if ((this.picEmailCcInput || '').trim()) this.addPicEmailCcTag();
        if (!this.formData.picEmailTo || this.formData.picEmailTo.length === 0) {
          this.errorField.picEmailTo = "At least one To email is required";
          hasError = true;
        }
      }

      for (const field of requiredFields) {
        if (
          this.formData[field] === null ||
          this.formData[field] === "" ||
          this.formData[field] === undefined
        ) {
          this.errorField[field] = "This field is required";
          hasError = true;
        }
      }

      return !hasError;
    },
    isValidEmail(s) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    },
    focusToInput() {
      this.$nextTick(() => {
        if (this.$refs.picEmailToInput) this.$refs.picEmailToInput.focus();
      });
    },
    focusCcInput() {
      this.$nextTick(() => {
        if (this.$refs.picEmailCcInput) this.$refs.picEmailCcInput.focus();
      });
    },
    addPicEmailToTag() {
      const val = (this.picEmailToInput || '').trim();
      if (!val) return;
      if (!this.isValidEmail(val)) {
        this.errorField.picEmailTo = `"${val}" is not a valid email address`;
        return;
      }
      if (this.formData.picEmailTo.indexOf(val) === -1) {
        this.formData.picEmailTo.push(val);
      }
      this.picEmailToInput = '';
      this.errorField.picEmailTo = null;
    },
    removePicEmailTo(index) {
      this.formData.picEmailTo.splice(index, 1);
    },
    addPicEmailCcTag() {
      const val = (this.picEmailCcInput || '').trim();
      if (!val) return;
      if (!this.isValidEmail(val)) {
        this.errorField.picEmailCc = `"${val}" is not a valid email address`;
        return;
      }
      if (this.formData.picEmailCc.indexOf(val) === -1) {
        this.formData.picEmailCc.push(val);
      }
      this.picEmailCcInput = '';
      this.errorField.picEmailCc = null;
    },
    removePicEmailCc(index) {
      this.formData.picEmailCc.splice(index, 1);
    },
    nextPage() {
      if (this.validatePage(this.currentPage)) {
        this.currentPage++;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    prevPage() {
      this.clearErrors();
      this.currentPage--;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    handleBack() {
      if (this.currentPage > 1) {
        this.prevPage();
      } else {
        this.$router.push({
          name: "HrdcApplication",
          query: {
            viewId: "all",
          },
        });
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.formData.bankAccountScreenshot = file;
      }
    },
    handleFileDrop(event) {
      this.fileDragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.formData.bankAccountScreenshot = file;
      }
    },
    toggleSubmitDialog() {
      if (!this.validatePage(this.currentPage)) {
        return;
      }

      this.$refs.confirmDialog.open({
        title: "Submit HRDC Application",
        message: "Are you sure you want to submit this application?",
        options: {
          btnDisagree: "Cancel",
          btnAgree: "Submit",
        },
        agree: () => {
          this.submitForm();
        },
        disagree: () => {},
      });
    },
    toUpper(val) {
      return typeof val === "string" ? val.toUpperCase() : val;
    },
    submitForm() {
      this.loadingDialog = true;

      const fd = new FormData();
      fd.append("companyName", this.toUpper(this.formData.companyName));
      fd.append("companySsmNo", this.toUpper(this.formData.companySsmNo));
      fd.append("companyAddress", this.toUpper(this.formData.companyAddress));
      fd.append("companyTinNo", this.toUpper(this.formData.companyTinNo));
      fd.append("taxpodType", this.formData.taxpodType);

      // Handle price — if "Other", use customPrice
      const price = this.formData.price === "Other"
        ? this.formData.customPrice
        : this.formData.price.replace(/[^0-9]/g, "");
      fd.append("price", price);

      fd.append("isPaymentMade", this.formData.isPaymentMade);

      // Payment mode — if "Other", use customPaymentMode
      if (this.formData.isPaymentMade === "Yes") {
        const paymentMode = this.formData.paymentMode === "Other"
          ? this.toUpper(this.formData.customPaymentMode)
          : this.formData.paymentMode;
        fd.append("paymentMode", paymentMode);

        // Bank details
        if (this.formData.bankAccountScreenshot) {
          fd.append("bankAccountScreenshot", this.formData.bankAccountScreenshot);
        }
        const specificBankName = this.formData.specificBankName === "Other"
          ? this.toUpper(this.formData.customBankName)
          : this.formData.specificBankName;
        fd.append("specificBankName", specificBankName);
        fd.append("accountNumber", this.toUpper(this.formData.accountNumber));
        fd.append("accountHolderName", this.toUpper(this.formData.accountHolderName));
      }

      // Enrollment details
      if (this.formData.isPaymentMade === "No") {
        // First account — persisted on Application table.
        fd.append("firstNameAccEnrollment", this.toUpper(this.formData.firstNameAccEnrollment));
        fd.append("lastNameAccEnrollment", this.toUpper(this.formData.lastNameAccEnrollment));
        fd.append("contactNoAccEnrollment", this.toUpper(this.formData.contactNoAccEnrollment));
        fd.append("emailAccEnrollment", this.formData.emailAccEnrollment);

        // Accounts 2..N — persisted on ApplicationEnrollmentDetail table.
        if (this.additionalEnrollmentDetails.length > 0) {
          const extras = this.additionalEnrollmentDetails.map((a) => ({
            firstNameAccEnrollment: this.toUpper(a.firstNameAccEnrollment),
            lastNameAccEnrollment: this.toUpper(a.lastNameAccEnrollment),
            contactNoAccEnrollment: this.toUpper(a.contactNoAccEnrollment),
            emailAccEnrollment: a.emailAccEnrollment,
          }));
          fd.append("additionalEnrollmentDetails", JSON.stringify(extras));
        }
      }

      // Client PIC
      fd.append("picName", this.toUpper(this.formData.picName));
      fd.append("picContactNumber", this.toUpper(this.formData.picContactNumber));
      fd.append("picEmailTo", JSON.stringify(this.formData.picEmailTo || []));
      fd.append("picEmailCc", JSON.stringify(this.formData.picEmailCc || []));

      // Referrer (logged-in user)
      fd.append("referById", this.auth.uuid);
      fd.append("referByName", this.auth.name);
      fd.append("referByEmail", this.auth.email);
      fd.append("referByContactNo", this.auth.mobile || "");

      this.apiSubmit.setParams(fd);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.loadingDialog = false;

          if (response.status) {
            this.$store.dispatch("showMessage", {
              message: "Application submitted successfully!",
              messageType: "success",
              timeout: 2000,
            });

            this.$router.push({
              name: "HrdcApplication",
              query: {
                viewId: "all",
              },
            });
          } else {
            this.$store.dispatch("showMessage", {
              message: response.message || "Failed to submit application",
              messageType: "error",
              timeout: 3000,
            });
          }
        } catch (err) {
          this.loadingDialog = false;
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   HRDC — ADD APPLICATION
   ----------------------------------------------------------------------------
   THIS PAGE IS A SHEET, NOT A STACK OF SLABS.

   The root is the routed <v-container>, so src/plugins/theme-glass.css §2 hands
   it the frosted panel, the rim, the lift and the WELD to the breadcrumb above
   — the same shell every other console page gets, from one place. That is
   exactly what the old markup was duplicating by hand: an #eaecef header slab,
   an #eaecef stepper slab and #eaecef cards, three tones of the same grey
   floating inside a panel that already floats. Four objects where the glass
   language wants two: chrome, and the page.

   So everything down to the first .section-card now paints CONTENT ON the
   sheet — the hero, the stepper rail and the action bar carry no surface of
   their own — and SURFACES are left to the glass layer. .section-card,
   .section-body, .section-header and .back-btn are all styled globally there
   at (1,1,0), which outranks every scoped rule in this file; restating their
   background/radius/shadow here would be dead CSS that reads as if it were in
   effect. What is left below is only what that layer cannot know: the light
   header band (its icons are `color="white"` → .white--text !important, so a
   stylesheet that blanks the band would leave white glyphs on white), this
   page's own widgets, and spacing.

   NO LITERAL COLOURS. Every value is a var(--app-*) resolved by theme-dark.css,
   which is what keeps the two themes in step from one edit — the dark half of
   this file is four rules now instead of the sixty it used to carry across two
   overlapping blocks.
   ============================================================================ */

/* ============================================
   PAGE SHEET
   ============================================ */
.app-add-page {
  /* Fills main minus the breadcrumb and footer. A `100vh - 77px` guess only
     subtracts the app bar and overflows — see .layout-main__content. */
  flex: 1 0 auto;
  padding: 28px 24px 56px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* The sheet spans the whole column like every other page; the FORM inside it
   keeps a readable measure instead of stretching one input across a 1600px
   monitor. Centring lives here, not on the sheet, because the sheet's width and
   margins are owned by theme-glass. */
.app-add-page__inner {
  width: 100%;
  max-width: 940px;
  margin: 0 auto;
}

/* ============================================
   HERO
   ============================================ */
.add-hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 20px;
}

/* Surface, rim, radius and hover come from theme-glass §5 — only the box the
   button needs is set here. */
.back-btn {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.back-btn:hover {
  color: var(--app-accent);
}

/* The icons below carry no `color` prop, so they inherit the colour of the
   button or circle around them and follow its hover/active state for free.
   Vuetify's own `.theme--light.v-icon` is (0,2,0); these are (0,2,0) plus the
   scope attribute, so they win without !important. */
.back-btn .v-icon,
.stepper__circle .v-icon,
.action-btn .v-icon {
  color: inherit;
}

.add-hero__info {
  flex: 1;
  min-width: 0;
}

/* The wizard's identity moves to the eyebrow so the h1 can be the STEP. The
   old title repeated "New Application - " on all three pages, which meant the
   only part that changed sat at the far end of an ellipsised line. */
.add-hero__eyebrow {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: var(--app-text-faint);
  margin-bottom: 3px;
}

.add-hero__title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
  letter-spacing: -0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-hero__step {
  display: inline-flex;
  align-items: baseline;
  padding: 6px 14px;
  border-radius: 999px;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
  color: var(--app-accent-strong);
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

.add-hero__step-total {
  opacity: 0.6;
  font-weight: 600;
}

/* ============================================
   STEPPER — a rail on the sheet, not a card
   ============================================ */
.stepper {
  padding-bottom: 22px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--app-border);
}

.stepper__track {
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepper__step {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.stepper__circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

/* Only the CURRENT step is a solid fill. Completed steps used to be a second
   saturated disc, so a wizard on page 3 showed three loud circles and no
   answer to "where am I"; they are a soft tile now and the ring does the
   pointing. */
.stepper__circle--active {
  background: var(--app-accent);
  color: var(--app-accent-fg);
  box-shadow: 0 0 0 4px var(--app-ring);
}

.stepper__circle--completed {
  background: var(--app-success-bg);
  border-color: var(--app-success-border);
  color: var(--app-success-fg);
}

.stepper__circle--upcoming {
  background: var(--app-form-tile);
  border-color: var(--app-border);
  color: var(--app-text-faint);
}

.stepper__number {
  font-size: 0.9rem;
  font-weight: 700;
}

.stepper__label {
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--app-text-faint);
  white-space: nowrap;
  transition: color 0.3s ease;
}

.stepper__label--active {
  color: var(--app-accent-strong);
  font-weight: 700;
}

.stepper__label--completed {
  color: var(--app-success-fg);
}

.stepper__line {
  width: 40px;
  height: 2px;
  border-radius: 2px;
  background: var(--app-border);
  margin: 0 8px;
  transition: background 0.3s ease;
  flex-shrink: 0;
}

.stepper__line--completed {
  background: var(--app-success);
}

/* ============================================
   SECTION CARDS
   --------------------------------------------
   Surface / rim / radius / lift / hover: theme-glass §4. Only the light band's
   paint and the interior spacing are this page's business.
   ============================================ */
.section-card {
  margin-bottom: 24px;
}

/* --app-brand-gradient, not a hand-rolled sky gradient: it is the token built
   for a saturated header in BOTH themes, and its -fg pairs with the white
   icons the template pins. Dark never sees it — theme-glass §4 neutralises the
   band app-wide from `html.dark-mode #app` (an id, so it wins). */
.section-header {
  background: var(--app-brand-gradient);
  padding: 15px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-header__title {
  color: var(--app-brand-gradient-fg);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
}

.section-body {
  padding: 26px 24px;
}

.section-description {
  font-size: 0.94rem;
  color: var(--app-text-muted);
  line-height: 1.6;
  margin-bottom: 22px;
  padding: 13px 18px;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
  border-left: 3px solid var(--app-accent);
  border-radius: var(--glass-radius-sm);
}

/* ============================================
   FORM GROUPS
   ============================================ */
.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group--indent {
  margin-left: 20px;
  padding-left: 18px;
  border-left: 2px solid var(--app-accent-border);
}

/* ============================================
   PARTICIPANT BLOCK (multi-pax enrollment)
   ============================================ */
.participant-block {
  border-top: 1px solid var(--app-border);
  padding-top: 22px;
  margin-top: 22px;
  margin-bottom: 22px;
}

.participant-block__badge {
  display: inline-block;
  background: var(--app-info-bg);
  color: var(--app-info-fg);
  font-size: 0.76rem;
  font-weight: 700;
  padding: 5px 13px;
  border-radius: 999px;
  margin-bottom: 18px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.form-group__label {
  display: block;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--app-text);
  margin-bottom: 7px;
  line-height: 1.5;
}

.required {
  color: var(--app-danger);
  font-weight: 700;
}

.form-group__info {
  margin-left: 4px;
  color: var(--app-text-faint) !important;
  cursor: help;
  vertical-align: -2px;
  transition: color 0.15s;
}

.form-group__info:hover {
  color: var(--app-accent) !important;
}

.form-group__helper {
  display: block;
  font-size: 0.8rem;
  color: var(--app-text-faint);
  margin-top: 5px;
  line-height: 1.4;
}

/* ============================================
   FILE UPLOAD AREA
   --------------------------------------------
   Same family as theme-glass's .file-card: a tile ONE step in from the card,
   so it takes the form-tile tier and no shadow — a dropzone that floats reads
   as a second card, not as a slot in this one.
   ============================================ */
.file-upload-area {
  border: 1px dashed var(--app-field-border);
  border-radius: var(--glass-radius-sm);
  padding: 24px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  background: var(--app-form-tile);
  text-align: center;
}

.file-upload-area:hover,
.file-upload-area--drag {
  border-color: var(--app-accent);
  background: var(--app-accent-soft);
}

.file-upload-area--error,
.file-upload-area--error:hover {
  border-color: var(--app-danger);
}

.file-upload-area__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
}

.file-upload-area__text {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--app-text-muted);
}

.file-upload-area__hint {
  font-size: 0.8rem;
  color: var(--app-text-faint);
}

.file-upload-area__selected {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}

.file-upload-area__filename {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--app-accent-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;
}

.file-upload-area__remove {
  cursor: pointer;
  transition: color 0.15s ease;
}

.file-upload-area__remove:hover {
  color: var(--app-danger) !important;
}

.file-upload-error {
  font-size: 0.8rem;
  color: var(--app-danger);
  margin-top: 5px;
}

/* ============================================
   ACTION BAR
   ============================================ */
.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding-top: 20px;
  border-top: 1px solid var(--app-border);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 30px;
  border-radius: var(--glass-radius-sm);
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  font-family: inherit;
}

.action-btn--primary {
  background: var(--app-accent);
  color: var(--app-accent-fg);
}

.action-btn--primary:hover {
  box-shadow: 0 4px 14px var(--app-accent-glow);
  transform: translateY(-1px);
}

.action-btn--secondary {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
  border-color: var(--app-border-strong);
}

.action-btn--secondary:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-field-border-hover);
  color: var(--app-text);
}

/* Submit is the page's one terminal action, so it takes the brand gradient
   rather than a second flat accent fill — Next and Submit were near-identical
   blues sitting in the same corner of consecutive pages. */
.action-btn--submit {
  background: var(--app-brand-gradient);
  color: var(--app-brand-gradient-fg);
}

.action-btn--submit:hover {
  background: var(--app-brand-gradient-hover);
  box-shadow: 0 4px 14px var(--app-brand-glow);
  transform: translateY(-1px);
}

/* ============================================
   TAG INPUT (To / Cc emails)
   ============================================ */
.tag-input {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--app-field-border);
  border-radius: var(--glass-radius-sm);
  background: var(--app-field);
  cursor: text;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
}

.tag-input:hover {
  border-color: var(--app-field-border-hover);
}

/* A RING on focus, not a 2px border with the padding shaved by 1px to
   compensate: the old pair reflowed every chip in the box by a pixel on focus
   and again on blur. */
.tag-input:focus-within {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.tag-input--error,
.tag-input--error:hover {
  border-color: var(--app-danger);
}

.tag-input--error:focus-within {
  border-color: var(--app-danger);
  box-shadow: 0 0 0 3px var(--app-ring-danger);
}

.tag-input__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--app-info-bg);
  color: var(--app-info-fg);
  border: 1px solid var(--app-accent-border);
  border-radius: 8px;
  padding: 3px 6px 3px 10px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
}

.tag-input__chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s;
}

.tag-input__chip-remove:hover {
  background: var(--app-hover);
}

.tag-input__input {
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  min-width: 140px;
  padding: 4px 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--app-text);
  font-family: inherit;
}

.tag-input__input::placeholder {
  color: var(--app-field-placeholder);
}

.tag-input__error {
  margin-top: 6px;
  font-size: 0.8rem;
  color: var(--app-danger);
  line-height: 1.3;
}

kbd {
  display: inline-block;
  padding: 1px 5px;
  font-family: 'SF Mono', Monaco, Consolas, monospace;
  font-size: 0.72rem;
  color: var(--app-neutral-fg);
  background: var(--app-neutral-bg);
  border: 1px solid var(--app-border-strong);
  border-radius: 4px;
}

/* ============================================
   DARK
   --------------------------------------------
   Everything above is token-driven, so dark needs only what a token cannot
   express: the section band, whose light paint is a saturated gradient. The
   band itself is already neutralised app-wide by theme-glass §4
   (`html.dark-mode #app .section-header` — an id, so it beats these rules on
   background). What is left is putting the accent back at CUE size rather than
   as a field, matching hrdc/views/application/View.vue and Edit.vue.
   ============================================ */
.dark-mode .section-header {
  position: relative;
}

/* 2px and faded top-to-bottom: it marks where the header starts without
   drawing a hard full-height rail. .section-card's overflow clips it to the
   card radius. */
.dark-mode .section-header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    180deg,
    rgba(var(--app-accent-rgb), 0.55),
    rgba(var(--app-accent-rgb), 0.12)
  );
}

.dark-mode .section-header__title {
  color: var(--app-text);
  letter-spacing: 1.6px;
}

/* The icon can hold the accent now that the band behind it is neutral.
   `color="white"` on the tag compiles to .white--text (0,1,0 + !important);
   this is (0,3,0 + !important) and wins. */
.dark-mode .section-header .v-icon {
  color: var(--app-accent) !important;
  opacity: 0.85;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter {
  transform: translateX(24px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-24px);
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 959px) {
  .app-add-page {
    padding: 20px 16px 44px !important;
  }

  .add-hero__title {
    font-size: 1.12rem;
  }

  .stepper {
    overflow-x: auto;
  }

  .stepper__track {
    min-width: 560px;
  }

  .stepper__label {
    font-size: 0.8rem;
  }

  .stepper__line {
    width: 26px;
  }

  .section-header {
    padding: 14px 18px;
  }

  .section-body {
    padding: 22px 18px;
  }

  .form-group--indent {
    margin-left: 12px;
    padding-left: 14px;
  }
}

@media (max-width: 599px) {
  .app-add-page {
    padding: 16px 12px 36px !important;
  }

  .add-hero {
    gap: 12px;
  }

  .add-hero__step {
    display: none;
  }

  .stepper__track {
    min-width: 480px;
  }

  .stepper__circle {
    width: 30px;
    height: 30px;
  }

  .stepper__number {
    font-size: 0.82rem;
  }

  .form-actions {
    gap: 10px;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
    padding: 12px 16px;
    font-size: 0.92rem;
  }
}
</style>

<style>
/* ============================================================================
   UNSCOPED: Vuetify input chrome for this page only.
   ----------------------------------------------------------------------------
   Unscoped because a few of the nodes below (the <fieldset>, the message list)
   are rendered by Vuetify outside this component's scope id. `.app-add-page`
   on every selector is what keeps the rules on this page — the file used to
   pair each rule with a `.dark-mode` twin; one token-driven rule now serves
   both themes.
   ============================================================================ */

.app-add-page .app-input.v-text-field--outlined > .v-input__control > .v-input__slot {
  border-radius: var(--glass-radius-sm) !important;
  min-height: 48px;
}

/* Vuetify draws the outlined border on a <fieldset> that is absolutely
   positioned 5px ABOVE .v-input__slot (top: -5px, to make room for the
   floating-label notch), so filling the SLOT leaves that 5px band under the top
   border unpainted and the card colour shows through as a gap — invisible while
   --app-field and --app-card were both #fff, obvious the moment the field tier
   differs from the card. Fill the fieldset instead: it owns the border box.
   Then lift the field content above it, since an absolutely positioned fieldset
   paints over its static siblings. */
.app-add-page .app-input.v-text-field--outlined fieldset {
  background: var(--app-field);
  border-color: var(--app-field-border) !important;
  border-radius: var(--glass-radius-sm) !important;
  transition: border-color 0.15s ease;
}

.app-add-page .app-input .v-input__slot > *:not(fieldset) {
  position: relative;
  z-index: 1;
}

.app-add-page .app-input.v-text-field--outlined:hover fieldset {
  border-color: var(--app-field-border-hover) !important;
}

.app-add-page .app-input.v-input--is-focused fieldset {
  border-color: var(--app-accent) !important;
  border-width: 2px !important;
}

.app-add-page .app-input .v-text-field__slot input,
.app-add-page .app-input .v-text-field__slot textarea,
.app-add-page .app-input.v-select .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.98rem !important;
  font-weight: 600 !important;
  color: var(--app-text) !important;
}

.app-add-page .app-input .v-text-field__slot input::placeholder,
.app-add-page .app-input .v-text-field__slot textarea::placeholder {
  color: var(--app-field-placeholder) !important;
  font-weight: 500 !important;
}

.app-add-page .app-input .v-label {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.98rem !important;
  color: var(--app-field-placeholder) !important;
}

/* The v-select chevron takes the theme's default ink, which on the dark field
   is nearly the field's own colour. */
.app-add-page .app-input .v-input__append-inner .v-icon {
  color: var(--app-text-faint) !important;
}

/* Error state */
.app-add-page .app-input.v-input--has-state fieldset,
.app-add-page .app-input.v-input--has-state:hover fieldset {
  border-color: var(--app-danger) !important;
}

.app-add-page .app-input .v-messages__message {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.8rem !important;
  color: var(--app-danger) !important;
}
</style>
